import {
  createAsyncThunk,
  createSlice,
  Draft,
  PayloadAction,
} from '@reduxjs/toolkit';
import focus from '../classes/focusController';
import {
  getValidators,
  hasSucceedValidator,
  IValidatorError,
  registerValidator,
  unregisterValidator,
} from '../components/common/previousValidation/validators';
import { IFieldDefinition } from '../types';
import { getValidations } from './validation';

export const ERROR = 'error';
export const SUCCESS = 'success';
export const VALIDATE = 'validate';

type TRef = HTMLInputElement;

export type TPossibleValues = unknown;

export interface IValidationState {
  errors: Record<string, string | undefined>;
  form: string;
  submitValues: Record<string, TPossibleValues>;
  refs: Record<string, TRef>;
  values: Record<string, TPossibleValues>;
}
export interface IPreviousValidationSlice {
  forms: Record<string, IValidationState>;
}

interface IValidateByName {
  form: string;
  name: string;
  ref?: TRef;
  tabId?: string;
  validations: IFieldDefinition['validate'];
}

function validateByName({
  form,
  name,
  ref,
  tabId,
  validations,
}: IValidateByName) {
  const { validate } = getValidations(validations);
  unregisterValidator({ form, name });
  registerValidator({
    form,
    name,
    tabId,
    validator: ({ values }) => {
      return new Promise((resolve) => {
        const result = validate(values[name]);
        if (result === true) resolve(true);
        else {
          resolve({
            error: result,
            element: () => {
              return ref ?? document.querySelector(`[name="${name}"]`);
            },
          });
        }
      });
    },
  });
}

/**
 * Determina si un proceso de validación culminó con éxito
 * o si hubo errores.
 */
export function hasSucceedValidation<T>(
  validationResult: TSuccessfulValidation<T> | TErrorValidation,
): validationResult is TSuccessfulValidation<T> {
  return 'submitValues' in validationResult;
}
export type TSuccessfulValidation<T = Record<string, unknown>> = {
  submitValues: T;
};
export type TErrorValidation<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  errors: Record<keyof T, string>;
};

export const validate = createAsyncThunk<
  TSuccessfulValidation | TErrorValidation,
  { form: string },
  { state: { previousValidationSlice: IPreviousValidationSlice } }
>('previousValidationSlice/validate', async ({ form }, { getState }) => {
  const state = getState().previousValidationSlice.forms[form];

  let newValues: Record<string, unknown> = {};

  const validatorsResult = (
    await Promise.all(
      (getValidators(form) ?? []).map((validatorObject) => {
        return new Promise<
          (IValidatorError & { name: string; tabId?: string }) | true
        >((resolve) => {
          validatorObject
            .validator({
              submitValues: state.submitValues,
              values: state.values,
            })
            .then((result) => {
              if (result === true) resolve(true);
              else if (hasSucceedValidator(result)) {
                newValues = { ...newValues, ...result.newValues };
                resolve(true);
              } else
                resolve({
                  error: result.error,
                  name: validatorObject.name,
                  element: result.element,
                  tabId: validatorObject.tabId,
                });
            })
            .catch((e) => {
              console.error(e);
            });
        });
      }),
    )
  ).filter(
    (result): result is IValidatorError & { name: string; tabId?: string } =>
      result !== true,
  );

  const firstValidatorError = validatorsResult[0];

  const errors: Record<string, string> = validatorsResult.reduce(
    (errorsObject, currentError) => {
      const newErrorObject = {
        ...errorsObject,
        [currentError.name]: currentError.error,
      };
      return newErrorObject;
    },
    {},
  );

  if (firstValidatorError) {
    const focusElement = firstValidatorError.element ?? null;
    const { error } = firstValidatorError;

    if (focusElement) {
      const focused = await focus.on(focusElement, {
        dispatchCustomEvent: true,
      });
      if (focused) {
        const input = focused as HTMLInputElement;
        input.setCustomValidity(error);
        input.reportValidity();
      }
    }
    return { errors };
  }

  const ret: TSuccessfulValidation = {
    submitValues: { ...state.submitValues, ...newValues },
  };

  return ret;
});

const previousValidationSlice = createSlice({
  initialState: { forms: {} } as IPreviousValidationSlice,
  name: 'previousValidationSlice',
  reducers: {
    error(
      state,
      { payload }: PayloadAction<Pick<IValidationState, 'errors' | 'form'>>,
    ) {
      state.forms[payload.form].errors = payload.errors;
    },
    init(
      state,
      { payload: { form } }: PayloadAction<Pick<IValidationState, 'form'>>,
    ) {
      state.forms[form] = {
        errors: {},
        form,
        refs: {},
        submitValues: {},
        values: {},
      };
    },
    setRef(
      state,
      {
        payload: { form, name, ref },
      }: PayloadAction<
        Pick<IValidationState, 'form'> & {
          name: string;
          ref: TRef;
        }
      >,
    ) {
      if (state.forms[form])
        state.forms[form].refs[name] =
          ref as unknown as Draft<HTMLInputElement>;
    },
    setValidations(
      state,
      {
        payload: {
          name,
          form,
          initialValue,
          initialSubmitValue,
          ref,
          tabId,
          validations,
        },
      }: PayloadAction<
        Pick<IValidationState, 'form'> & {
          name?: string;
          initialValue?: unknown;
          initialSubmitValue?: unknown;
          ref?: TRef;
          tabId?: IValidateByName['tabId'];
          validations?: IValidateByName['validations'];
        }
      >,
    ) {
      if (name === undefined) return;

      validateByName({
        form,
        name,
        ref,
        tabId,
        validations,
      });

      if (state.forms[form].values[name] === undefined) {
        state.forms[form].values[name] = initialValue;
        state.forms[form].submitValues[name] = initialSubmitValue;
      }
    },
    setValue(
      state,
      {
        payload,
      }: PayloadAction<
        Pick<IValidationState, 'form'> & {
          name: string;
          submitValue?: unknown;
          value: TPossibleValues;
        }
      >,
    ) {
      state.forms[payload.form].values[payload.name] = payload.value;
      state.forms[payload.form].submitValues[payload.name] =
        payload.submitValue;
      const input =
        state.forms[payload.form].refs[payload.name] ??
        (document.getElementsByName(payload.name)[0] as HTMLInputElement);
      input?.setCustomValidity('');
      state.forms[payload.form].errors[payload.name] = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      validate.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { form },
          },
        },
      ) => {
        if (!hasSucceedValidation(payload)) {
          state.forms[form].errors = payload.errors;
        } else {
          state.forms[form].submitValues = payload.submitValues as Record<
            string,
            TPossibleValues
          >;
        }
      },
    );
  },
});

export const previousValidationActions = {
  ...previousValidationSlice.actions,
  validate,
};
export default previousValidationSlice.reducer;
